import React, { useState } from 'react';
import { usePost } from '../../../API';
import { ActionsDropdown, Badge, Button, Card, CardBody, CardHeader, Icon, useToast } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import type * as survey from '@aidkitorg/types/lib/survey';
import { downloadDataDictionary } from '../utils/downloadDataDictionary';

interface DataDictionaryCardProps {
  component: survey.DataDictionary;
  /** Static widget ID from the dashboard widget library */
  staticWidgetId?: string;
  /** When true, shows delete action in card header */
  isEditMode?: boolean;
  /** Callback to delete this component from the dashboard */
  onDelete?: () => void;
}

/**
 * Renders a Data Dictionary download as a card with title and centered download button,
 * matching the DownloadOnlyCard pattern.
 */
export const DataDictionaryCard: React.FC<DataDictionaryCardProps> = ({ component, staticWidgetId, isEditMode, onDelete }) => {
  const L = useLocalizedStrings();
  const getDataDictionary = usePost('/survey/get_data_dictionary');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fileType = component.type || 'csv';

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      await downloadDataDictionary({
        fileType,
        getDataDictionary: () => getDataDictionary({
          limitToSubsurvey: component.limitToSubsurvey,
          excludeFields: component.excludeFields,
          includeComputations: component.includeComputations,
          excludeKinds: component.excludeKinds,
        }),
        onUnsupportedType: (type) => toast({
          description: 'Unrecognized DataDictionary type: ' + type,
          variant: 'error',
        }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAction = isEditMode && onDelete ? (
    <ActionsDropdown
      actions={[{
        label: L.admin_panel.dashboards.delete,
        onClick: onDelete,
        icon: 'TrashIcon',
        variant: 'warn' as const,
      }]}
      item={component}
    />
  ) : undefined;

  const title = (
    <div className="flex flex-wrap items-center gap-2 text-sm px-4 py-2">
      Data Dictionary{component.limitToSubsurvey ? ` (${component.limitToSubsurvey})` : ''}
      {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
    </div>
  );

  return (
    <Card className="rounded-md h-full w-full">
      <CardHeader title={title} actionButton={deleteAction} />
      <CardBody className="flex-1 flex flex-col items-center justify-center gap-3">
        <span className="text-sm text-gray-500">dataDictionary.{fileType}</span>
        <Button
          variant="secondary"
          loading={isLoading}
          onClick={handleDownload}
          icon={<Icon name="ArrowDownTrayIcon" type="solid" role="decorative" />}
        >
          {L.dashboard.download}
        </Button>
      </CardBody>
    </Card>
  );
};
